import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../lib/api';
import { rememberOfflineOwner, readOfflineOwner, forgetOfflineOwner } from '../lib/offlineOwner';

const AuthCtx = createContext({ user: null, loading: true, refresh: () => {}, logout: () => {} });

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [offlineOwner, setOfflineOwner] = useState(null);
    useEffect(() => { if (user?.user_id) { setOfflineOwner(null); rememberOfflineOwner(user); } }, [user]);

    const refresh = useCallback(async ({ preserveOnError = false } = {}) => {
        try {
            if (navigator.onLine === false) {
                setOfflineOwner(await readOfflineOwner());
                if (!preserveOnError) setUser(null);
                return false;
            }
            const r = await api.get('/auth/me', { timeout: 10000 });
            if (!r.data || typeof r.data !== 'object' || Array.isArray(r.data) || !r.data.user_id) {
                throw new Error('Invalid authentication response');
            }
            setUser(r.data);
            // Sync frontend locale → backend if they disagree (e.g. legacy Danish account using EN UI).
            // Backend uses this for AI prompts, watering task language, push notifications.
            try {
                const uiLocale = localStorage.getItem('locale') || 'en';
                if (r.data?.locale && r.data.locale !== uiLocale) {
                    await api.patch('/auth/me', { locale: uiLocale });
                    setUser({ ...r.data, locale: uiLocale });
                }
            } catch (e) { /* non-blocking */ }
            return true;
        } catch (e) {
            if (e?.response?.status === 401 || e?.response?.status === 403) { forgetOfflineOwner(); setOfflineOwner(null); }
            else if (!e?.response) setOfflineOwner(await readOfflineOwner());
            if (e?.response?.status !== 401) console.error('[auth] refresh failed', e);
            // A temporary network error after a verified purchase is not a
            // logout. Explicit authentication rejection still clears the user.
            if (!preserveOnError || e?.response?.status === 401) setUser(null);
            return false;
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        // CRITICAL: If returning from OAuth callback, skip /me check.
        // AuthCallback will exchange the session_id and establish the session first.
        if (window.location.hash?.includes('session_id=')) {
            setLoading(false);
            return;
        }
        refresh();
    }, [refresh]);

    useEffect(() => {
        const reconnect = () => refresh();
        window.addEventListener('online', reconnect);
        return () => window.removeEventListener('online', reconnect);
    }, [refresh]);

    const logout = useCallback(async () => {
        forgetOfflineOwner();
        setOfflineOwner(null);
        try {
            await api.post('/auth/logout');
        } catch (e) {
            console.error('[auth] logout request failed', e);
        }
        try { localStorage.removeItem('session_token'); } catch (e) {
            console.error('[auth] localStorage clear failed', e);
        }
        setUser(null);
        window.location.href = '/login';
    }, []);

    const value = useMemo(
        () => ({ user, setUser, loading, refresh, logout, offlineOwner }),
        [user, loading, refresh, logout, offlineOwner],
    );

    return (
        <AuthCtx.Provider value={value}>
            {children}
        </AuthCtx.Provider>
    );
}

export function useAuth() { return useContext(AuthCtx); }
